import { useEffect, useState } from 'react';

import { adminApi } from '../adminApi';
import {
  ingestRowKey,
  parseIngestRowKey,
  type IngestUsageSummary,
  type SocketBreakdown,
} from '../ingestUsage';
import { IngestUsageView, type SocketState } from './IngestUsageView';

// Usage → Ingest and storage (ADR 0209). Per boat and UTC day of receipt: rows
// written to telemetry, samples dropped, duplicates skipped, re-sent windows,
// and the bytes of the boat's telemetry frames and of its whole WebSocket in
// both directions, with the socket traffic of one selected boat and day split
// by message type. Below that, the table and database sizes the nightly job
// records. Read-only observability, so it lives with the Usage pages. The page
// loads the data; IngestUsageView renders it.

const WINDOWS = [7, 30, 90];

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { body?: { message?: string } };
  return e.body?.message ?? fallback;
}

export function AdminIngestUsage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<IngestUsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [socket, setSocket] = useState<SocketState | null>(null);

  useEffect(() => {
    // A slower request for an earlier window must not overwrite a newer one.
    let ignore = false;
    setData(null);
    setError(null);
    adminApi<IngestUsageSummary>(`/v1/admin/ingest-usage?days=${days}`)
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (!ignore) setError(errorMessage(err, 'Failed to load ingest usage.'));
      });
    return () => {
      ignore = true;
    };
  }, [days]);

  // Keep the selected boat and day while it is still in the window; otherwise
  // select the newest row.
  useEffect(() => {
    if (data === null) return;
    const keys = data.ingest.map(ingestRowKey);
    setSelected((current) =>
      current !== null && keys.includes(current) ? current : (keys[0] ?? null)
    );
  }, [data]);

  useEffect(() => {
    const target = selected === null ? null : parseIngestRowKey(selected);
    if (target === null) {
      setSocket(null);
      return;
    }
    let ignore = false;
    setSocket({ status: 'loading' });
    const query = new URLSearchParams({ boatId: target.boatId, day: target.day });
    adminApi<SocketBreakdown>(`/v1/admin/ingest-usage/socket?${query.toString()}`)
      .then((breakdown) => {
        if (!ignore) setSocket({ status: 'ready', breakdown });
      })
      .catch((err) => {
        if (!ignore) {
          setSocket({
            status: 'error',
            message: errorMessage(err, 'Failed to load socket traffic.'),
          });
        }
      });
    return () => {
      ignore = true;
    };
  }, [selected]);

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Ingest and storage</h1>
        <span className="admin-count admin-seg">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className="admin-link-button"
              disabled={w === days}
              onClick={() => setDays(w)}
            >
              {w}d
            </button>
          ))}
        </span>
      </header>
      <p className="admin-hint">
        What each boat sent over the last {days} days, per UTC day of receipt. Accepted rows were
        written to telemetry. Rejected samples failed the telemetry policy or the frame checks.
        Duplicates were already stored. Socket bytes count every message on the boat connection,
        heartbeats and acks included.
      </p>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}
      {data !== null ? (
        <IngestUsageView data={data} selected={selected} onSelect={setSelected} socket={socket} />
      ) : null}
    </section>
  );
}
